<script lang="ts">
  /**
   * The optional 25-minute tomato countdown (ARCHITECTURE.md §6, D5).
   *
   * Four states, and the fourth is the interesting one:
   *
   *     idle --[Start]--> running --[Pause]--> paused --[Resume]--> running
   *                                                                   |
   *                                          [zero] <-----------------┘
   *                                            |
   *                                         elapsed   ← terminal
   *
   * **Elapsed has no edge back to `running`.** D7 §9.3 overturned the draft's
   * "keep working" option on purpose: at zero the user resolves the task one way
   * or the other, which is the whole of "stop dead". `[Re-enter]` and
   * `[Finished]` are the only controls it renders, and `[Start]`/`[Reset]` are
   * gone — a timer that could be restarted would be a suggestion rather than a
   * prompt.
   *
   * Elapsed time comes from an absolute `endsAt` deadline, never from
   * decrementing a counter per tick. Browsers throttle `setInterval` in a hidden
   * tab, so a decrementing counter drifts by minutes — and the user who
   * backgrounded the tab mid-task is exactly the user AF1 says to stop the
   * instant time is up.
   *
   * It authors no operation and writes no session field, at any point in the
   * cycle. That is the same principle as the 4-hour timeout: a timeout is a
   * prompt, never notebook history (§4).
   *
   * R4.2: at zero the chime REPEATS (every 2s) until the user silences it with
   * `[ Stop ]` or leaves the prompt. `[ Stop ]` only silences — it authors
   * nothing and resolves nothing; the terminal prompt is otherwise unchanged.
   */
  import Icon from './Icon.svelte';
  import Check from 'lucide/dist/esm/icons/check.mjs';
  import Pause from 'lucide/dist/esm/icons/pause.mjs';
  import Play from 'lucide/dist/esm/icons/play.mjs';
  import RotateCcw from 'lucide/dist/esm/icons/rotate-ccw.mjs';
  import RotateCw from 'lucide/dist/esm/icons/rotate-cw.mjs';
  import VolumeX from 'lucide/dist/esm/icons/volume-x.mjs';
  import { playChime, primeChime, TIMER_CUES, type TimerCueId } from '../services/chime';
  import { getMeta, setMeta } from '../storage/db';

  interface Props {
    /** Default 25 minutes. A prop so it stays a one-line change (D5). */
    durationMs?: number;
    /** Fired once per run, at zero. */
    onElapsed?: () => void;
    /** The prompt's two ways out (D7). */
    onReenter?: () => void;
    onFinished?: () => void;
    /**
     * Ruling 2026-10-16: the host silences the alarm from OUTSIDE — choosing
     * re-entry (by either button) stops the repeats while the new items are
     * typed. One-way, like `silenced`: a cancelled sheet does not resume the
     * ringing.
     */
    muted?: boolean;
  }

  const {
    durationMs = 25 * 60_000,
    onElapsed,
    onReenter,
    onFinished,
    muted = false,
  }: Props = $props();

  type Phase = 'idle' | 'running' | 'paused' | 'elapsed';

  /** How often the display is refreshed. The clock is not read here. */
  const TICK_MS = 250;

  /**
   * R4.2: the alarm's repeat cadence at zero. The first chime fires in
   * `reachZero`; this drives the repeats until the user silences it.
   */
  const CHIME_REPEAT_MS = 2000;

  let phase = $state<Phase>('idle');
  /** Absolute wall-clock deadline; `null` unless running. */
  let endsAt = $state<number | null>(null);
  // svelte-ignore state_referenced_locally
  let remaining = $state(durationMs);
  /** Guards the one-shot chime and `onElapsed`. */
  let announced = $state(false);
  /** R4.2: the user has silenced the repeating alarm (or it was never armed). */
  let silenced = $state(false);

  /**
   * R5.3: the timer's sound, one of the named cues. `chime` — the sound the
   * timer shipped with — is the default, so today's behaviour is the no-change
   * default; the choice is device-local through the meta idiom and the next
   * zero simply reads this state, so there is nothing to re-prime.
   */
  let cue = $state<TimerCueId>('chime');

  // Load the persisted cue once: this effect reads no reactive state, so it
  // runs on mount only.
  $effect(() => {
    void (async () => {
      const saved = await getMeta<string>('timerCue');
      if (saved && (TIMER_CUES as readonly string[]).includes(saved)) {
        cue = saved as TimerCueId;
      }
    })();
  });

  /** The picker: persist the dropdown's choice immediately. */
  function setCue(next: string) {
    cue = next as TimerCueId;
    void setMeta('timerCue', cue);
  }

  /**
   * Recomputes from the deadline.
   *
   * `endsAt` rather than `remaining - TICK_MS` is the whole point: a tick that
   * arrives four minutes late reports the truth rather than four minutes of
   * optimism.
   */
  function recompute(): void {
    if (endsAt === null) return;
    const left = endsAt - Date.now();
    remaining = left > 0 ? left : 0;
    if (left <= 0 && phase !== 'elapsed') reachZero();
  }

  function reachZero() {
    phase = 'elapsed';
    endsAt = null;
    remaining = 0;
    if (announced) return;
    announced = true;
    playChime(cue);
    onElapsed?.();
  }

  $effect(() => {
    if (phase !== 'running') return;
    const handle = setInterval(recompute, TICK_MS);
    return () => clearInterval(handle);
  });

  /**
   * R4.2: the alarm repeats until it is stopped. While the prompt is up and the
   * user has not silenced it, re-chime on a fixed cadence (the first chime
   * already fired in `reachZero`). The cleanup silences it the instant the
   * component unmounts — resolving the task, `Esc`, or any other exit.
   * `muted` is the host's own silence (the re-enter choice, ruling 2026-10-16).
   */
  $effect(() => {
    if (phase !== 'elapsed' || silenced || muted) return;
    const handle = setInterval(() => playChime(cue), CHIME_REPEAT_MS);
    return () => clearInterval(handle);
  });

  function start() {
    announced = false;
    silenced = false;
    // R4.2 bug fix: unlock audio now, in the [Start] gesture — the chime fires
    // at zero, minutes later, when a cold AudioContext would be suspended.
    primeChime();
    endsAt = Date.now() + remaining;
    phase = 'running';
  }

  /** Freezes the remaining milliseconds — `endsAt` is simply dropped. */
  function pause() {
    recompute();
    endsAt = null;
    phase = 'paused';
  }

  /** Continues from the frozen remainder, not from the full duration. */
  function resume() {
    // Re-arm audio on the gesture too, in case the context was suspended.
    primeChime();
    endsAt = Date.now() + remaining;
    phase = 'running';
  }

  /** Back to the full duration, stopped. */
  function reset() {
    endsAt = null;
    remaining = durationMs;
    phase = 'idle';
    announced = false;
    silenced = false;
  }

  /** `[ Stop ]`: silences the repeating alarm. Authors nothing; the prompt stays. */
  function stopChime() {
    silenced = true;
  }

  const toggleLabel = $derived(
    phase === 'running' ? 'Pause' : phase === 'paused' ? 'Resume' : 'Start',
  );

  /** Fraction elapsed, for the progress ring. */
  const progress = $derived(durationMs === 0 ? 1 : 1 - remaining / durationMs);

  /** Zero-padded `m:ss`, so the readout does not jitter in width as it counts. */
  const clock = $derived(
    `${String(Math.floor(remaining / 60_000)).padStart(2, '0')}:${String(
      Math.floor((remaining % 60_000) / 1000),
    ).padStart(2, '0')}`,
  );

  /** `stroke-dashoffset` around a 44-diameter ring. */
  const RADIUS = 18;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  /**
   * Ring stroke width, in the 44-unit viewBox. Drawn as an attribute — not the
   * scoped stylesheet — so the thickness is one constant alongside the rest of
   * the ring geometry. 3 units was too thin to read on the 26px ring.
   */
  const STROKE_WIDTH = 5;
</script>

<div class="tomato" data-testid="tomato-timer" data-phase={phase}>
  {#if phase === 'elapsed'}
    <div class="tomato-prompt" data-testid="tomato-prompt" aria-live="assertive">
      <p class="tomato-copy">Time's up — stop dead. Re-enter it or mark it finished.</p>
      <div class="tomato-actions">
        <button class="action" type="button" data-testid="tomato-reenter" onclick={onReenter}>
          <Icon node={RotateCcw} />
          Re-enter
        </button>
        <button class="action" type="button" data-testid="tomato-finish" onclick={onFinished}>
          <Icon node={Check} />
          Finished
        </button>
        {#if !silenced && !muted}
          <!-- R4.2: silences the repeating alarm — not a resolution. -->
          <button class="action" type="button" data-testid="tomato-stop" onclick={stopChime}>
            <Icon node={VolumeX} />
            Stop
          </button>
        {/if}
      </div>
    </div>
  {:else}
    <svg class="tomato-ring" viewBox="0 0 44 44" aria-hidden="true">
      <circle class="tomato-track" cx="22" cy="22" r={RADIUS} stroke-width={STROKE_WIDTH} />
      <!--
        The arc fills as time elapses: `stroke-dashoffset` starts at the whole
        circumference (the dash pushed out of view → an empty ring) and shrinks
        to 0 (the dash fully drawn → a full ring). The 240ms CSS transition on
        `stroke-dashoffset` smooths each 250ms step. (A stale `--offset` custom
        property once fed a value nothing consumed, so the ring never moved.)
      -->
      <circle
        class="tomato-arc"
        cx="22"
        cy="22"
        r={RADIUS}
        stroke-width={STROKE_WIDTH}
        stroke-dasharray={CIRCUMFERENCE.toFixed(2)}
        stroke-dashoffset={(CIRCUMFERENCE * (1 - progress)).toFixed(2)}
      />
    </svg>

    <span class="tomato-clock" data-testid="tomato-timer-remaining">{clock}</span>

    <button class="action" type="button" data-testid="tomato-timer-toggle" onclick={() => {
      if (phase === 'running') pause();
      else if (phase === 'paused') resume();
      else start();
    }}>
      {#if phase === 'running'}<Icon node={Pause} />{:else}<Icon node={Play} />{/if}
      {toggleLabel}
    </button>

    {#if phase !== 'idle'}
      <button class="action" type="button" data-testid="tomato-timer-reset" onclick={reset}>
        <Icon node={RotateCw} />
        Reset
      </button>
    {/if}
  {/if}

  <!-- R5.3, amended: the cue is a dropdown — every option visible at once,
       one change persists. The choice takes effect at the next zero, which
       just reads `cue` — nothing to re-prime. It lives OUTSIDE the phase
       block on purpose: happy-dom 15 removes a `<select>` wrongly when a
       Svelte block tears it down (its no-arg remove() detaches the element,
       not the selected option), and the throw poisoned every timer effect.
       Rendered always, hidden by CSS while the prompt is up. -->
  <select
    class="cue"
    data-testid="tomato-timer-cue"
    aria-label="Timer chime"
    value={cue}
    onchange={(e) => setCue(e.currentTarget.value)}
  >
    <option value="chime">chime</option>
    <option value="bell">bell</option>
    <option value="soft">soft</option>
  </select>
</div>

<style>
  .tomato {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-family: var(--font-ui);
    font-size: 14px;
    color: var(--ink-muted);
  }

  .tomato-ring {
    width: 34px;
    height: 34px;
    transform: rotate(-90deg);
  }

  .tomato-track,
  .tomato-arc {
    fill: none;
  }

  .tomato-track {
    stroke: color-mix(in srgb, var(--ink) 14%, transparent);
  }

  .tomato-arc {
    stroke: var(--notice-border);
    stroke-linecap: round;
    transition: stroke-dashoffset 240ms linear;
  }

  .tomato-clock {
    font-variant-numeric: tabular-nums;
    min-width: 4ch;
  }

  .tomato-prompt {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid color-mix(in srgb, var(--notice-border) 34%, transparent);
    border-radius: 4px;
    background: var(--notice-surface);
  }

  .tomato-copy {
    margin: 0;
    color: var(--ink);
  }

  .tomato-actions {
    display: flex;
    gap: 8px;
  }

  /* R5.3, amended: the cue picker is a native select — it reads as a setting,
     not a peer of Start/Pause. Hidden while the zero prompt is up: the prompt
     offers the two resolutions and nothing else. */
  .cue {
    font: inherit;
    font-size: 12px;
    color: var(--ink-muted);
    background: none;
    border: 1px solid color-mix(in srgb, var(--ink) 50%, transparent);
    border-radius: 4px;
    padding: 4px 6px;
  }

  .tomato[data-phase='elapsed'] .cue {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .tomato-arc {
      transition: none;
    }
  }
</style>
